import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Dialog,
  Box,
  Typography,
  InputBase,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Fade,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TerminalIcon from '@mui/icons-material/Terminal';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DynamicFeedOutlinedIcon from '@mui/icons-material/DynamicFeedOutlined';
import AccessibilityNewOutlinedIcon from '@mui/icons-material/AccessibilityNewOutlined';

import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';

export const CommandPalette = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const {
    language,
    setLanguage,
    availableLanguages,
    t,
    servicesList,
    contactInfo,
    commandPaletteConfig,
    isSectionVisible
  } = useLanguage();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showShortcutsCheatSheet, setShowShortcutsCheatSheet] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Configuration from Dashboard - OFF by default on public frontend
  const config = commandPaletteConfig || {};
  const isEnabled = Boolean(config.enabled === true);
  const shortcutKey = (config.shortcut_key || 'k').toLowerCase();
  const showFloatingTrigger = isEnabled && config.show_floating_trigger === true;

  // Platform detection for ⌘ vs Ctrl
  const isMac = typeof window !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const modifierLabel = isMac ? '⌘' : 'Ctrl';

  // Localized placeholder
  const placeholder = config.placeholder?.[language] ||
    config.placeholder?.en ||
    (language === 'ge'
      ? 'მოძებნეთ სერვისები, მოქმედებები ან სექციები (Ctrl+K)...'
      : language === 'ru'
        ? 'Поиск услуг, действий или разделов (Ctrl+K)...'
        : 'Type a command, search services, or jump to section...');

  // Helper to open/close
  const handleOpen = useCallback(() => {
    if (!isEnabled) return;
    setOpen(true);
    setQuery('');
    setSelectedIndex(0);
    setShowShortcutsCheatSheet(false);
  }, [isEnabled]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Global Keyboard listener
  useEffect(() => {
    if (!isEnabled) return;

    const handleKeyDown = (e) => {
      // 1. Primary trigger: Ctrl/Cmd + [shortcutKey]
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === shortcutKey) {
        e.preventDefault();
        setOpen((prev) => !prev);
        return;
      }

      // 2. Secondary quick trigger: "/" when not focused on an input/textarea
      if (shortcutKey === '/' || e.key === '/') {
        const tag = document.activeElement ? document.activeElement.tagName.toLowerCase() : '';
        const isEditable = document.activeElement && document.activeElement.isContentEditable;
        if (tag !== 'input' && tag !== 'textarea' && tag !== 'select' && !isEditable && !open) {
          e.preventDefault();
          handleOpen();
          return;
        }
      }

      // 3. Quick help trigger: "?" when palette is open or globally
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        const tag = document.activeElement ? document.activeElement.tagName.toLowerCase() : '';
        if (tag !== 'input' && tag !== 'textarea') {
          if (!open) {
            handleOpen();
            setShowShortcutsCheatSheet(true);
          } else {
            setShowShortcutsCheatSheet((prev) => !prev);
          }
        }
      }
    };

    const handleCustomEvent = () => handleOpen();
    const handleToggleEvent = () => setOpen((prev) => !prev);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('itforsec:open-command-palette', handleCustomEvent);
    window.addEventListener('itforsec:toggle-command-palette', handleToggleEvent);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('itforsec:open-command-palette', handleCustomEvent);
      window.removeEventListener('itforsec:toggle-command-palette', handleToggleEvent);
    };
  }, [isEnabled, shortcutKey, open, handleOpen]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
    }
  }, [open]);

  // Smooth scroll helper
  const navigateTo = useCallback((hash) => {
    handleClose();
    if (hash.startsWith('/')) {
      window.location.href = hash;
      return;
    }
    const element = document.querySelector(hash);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Accessibility focus
      element.focus?.({ preventScroll: true });
    } else {
      window.location.hash = hash;
    }
  }, [handleClose]);

  // Copy email helper
  const handleCopyEmail = useCallback((email) => {
    if (!email) return;
    navigator.clipboard?.writeText(email);
    setCopySuccess(true);
    setTimeout(() => {
      setCopySuccess(false);
      handleClose();
    }, 900);
  }, [handleClose]);

  // Build the comprehensive commands list
  const allCommands = useMemo(() => {
    const list = [];

    // --- CATEGORY: NAVIGATION ---
    if (!isSectionVisible || isSectionVisible('hero')) {
      list.push({
        id: 'nav-home',
        category: 'Navigation',
        title: t('home') || 'Home',
        subtitle: 'Jump to top hero banner and overview',
        icon: <HomeOutlinedIcon />,
        action: () => navigateTo('#home'),
        keywords: 'home start top banner'
      });
    }

    if (!isSectionVisible || isSectionVisible('services')) {
      list.push({
        id: 'nav-services',
        category: 'Navigation',
        title: t('services') || 'Services',
        subtitle: 'Explore IT support, development & cybersecurity services',
        icon: <BuildOutlinedIcon />,
        action: () => navigateTo('#services'),
        keywords: 'services solutions offerings webdev security'
      });
    }

    if (!isSectionVisible || isSectionVisible('about')) {
      list.push({
        id: 'nav-about',
        category: 'Navigation',
        title: t('about') || 'About Us',
        subtitle: 'Learn about ITFORSEC mission, team & approach',
        icon: <InfoOutlinedIcon />,
        action: () => navigateTo('#about'),
        keywords: 'about company mission team background'
      });
    }

    if (!isSectionVisible || isSectionVisible('features')) {
      list.push({
        id: 'nav-features',
        category: 'Navigation',
        title: t('features') || 'Key Features & Capabilities',
        subtitle: 'Infrastructure management, automation & consulting',
        icon: <DynamicFeedOutlinedIcon />,
        action: () => navigateTo('#features'),
        keywords: 'features capabilities specs details automation'
      });
    }

    if (!isSectionVisible || isSectionVisible('testimonials')) {
      list.push({
        id: 'nav-testimonials',
        category: 'Navigation',
        title: t('testimonials') || 'Client Testimonials',
        subtitle: 'Read reviews and client experiences',
        icon: <StarBorderOutlinedIcon />,
        action: () => navigateTo('#testimonials'),
        keywords: 'testimonials reviews feedback ratings clients'
      });
    }

    if (!isSectionVisible || isSectionVisible('faq')) {
      list.push({
        id: 'nav-faq',
        category: 'Navigation',
        title: t('faq') || 'Frequently Asked Questions',
        subtitle: 'Common questions on response time, pricing & support',
        icon: <HelpOutlineOutlinedIcon />,
        action: () => navigateTo('#faq'),
        keywords: 'faq questions help answers support'
      });
    }

    if (!isSectionVisible || isSectionVisible('contact')) {
      list.push({
        id: 'nav-contact',
        category: 'Navigation',
        title: t('contact') || 'Contact Us',
        subtitle: 'Send an inquiry or request an audit',
        icon: <MailOutlineIcon />,
        action: () => {
          navigateTo('#contact');
          setTimeout(() => {
            const input = document.getElementById('name') || document.querySelector('#contact input');
            input?.focus();
          }, 400);
        },
        keywords: 'contact message quote audit inquiry form'
      });
    }

    if (!isSectionVisible || isSectionVisible('newsletter')) {
      list.push({
        id: 'nav-newsletter',
        category: 'Navigation',
        title: 'Newsletter Subscription',
        subtitle: 'Join IT briefings and security advisories',
        icon: <MailOutlineIcon />,
        action: () => navigateTo('#newsletter'),
        keywords: 'newsletter subscribe briefings updates email'
      });
    }

    list.push({
      id: 'nav-accessibility',
      category: 'Navigation',
      title: 'Accessibility Statement',
      subtitle: 'View WCAG 2.1 compliance and keyboard navigation details',
      icon: <AccessibilityNewOutlinedIcon />,
      action: () => navigateTo('/accessibility'),
      keywords: 'accessibility a11y wcag screen reader keyboard'
    });

    // --- CATEGORY: SPECIFIC SERVICES ---
    if (Array.isArray(servicesList) && servicesList.length > 0) {
      servicesList.forEach((srv, idx) => {
        const title = typeof srv.title === 'object'
          ? (srv.title[language] || srv.title.en || Object.values(srv.title)[0] || '')
          : (t(`service${idx + 1}`) || srv.title || `Service ${idx + 1}`);

        const desc = typeof srv.desc === 'object'
          ? (srv.desc[language] || srv.desc.en || Object.values(srv.desc)[0] || '')
          : (t(`service${idx + 1}Desc`) || srv.desc || '');

        list.push({
          id: `srv-${srv.id || idx}`,
          category: 'Services',
          title: title,
          subtitle: desc,
          icon: <SecurityOutlinedIcon />,
          action: () => {
            navigateTo('#services');
            setTimeout(() => {
              const card = document.getElementById(`service-card-${idx}`) || document.getElementById(`service-${idx}`);
              if (card) {
                card.scrollIntoView({ behavior: 'smooth', block: 'center' });
                card.style.transition = 'all 0.4s ease';
                card.style.boxShadow = '0 0 25px rgba(46, 91, 255, 0.7)';
                setTimeout(() => {
                  card.style.boxShadow = '';
                }, 2200);
              }
            }, 300);
          },
          keywords: `service ${title} ${desc}`
        });
      });
    }

    // --- CATEGORY: APPEARANCE & THEME ---
    list.push({
      id: 'theme-toggle',
      category: 'Appearance',
      title: isDarkMode ? 'Switch to Light Theme' : 'Switch to Dark Theme',
      subtitle: `Currently in ${isDarkMode ? 'Cyber Dark' : 'Clean Light'} mode`,
      icon: isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />,
      shortcut: 'T',
      action: () => {
        toggleTheme();
        handleClose();
      },
      keywords: 'theme dark light mode color style appearance'
    });

    // --- CATEGORY: LANGUAGES ---
    const langs = availableLanguages || {
      en: { name: 'English', flag: '🇬🇧' },
      ge: { name: 'Georgian (ქართული)', flag: '🇬🇪' },
      ru: { name: 'Russian (Русский)', flag: '🇷🇺' }
    };

    Object.entries(langs).forEach(([code, item]) => {
      const name = typeof item === 'object' ? item.name : item;
      const flag = typeof item === 'object' ? item.flag : '';
      const isCurrent = language === code;
      list.push({
        id: `lang-${code}`,
        category: 'Language',
        title: `${flag || '🌐'} ${name}`,
        subtitle: isCurrent ? 'Active language' : `Switch website language to ${name}`,
        icon: <TranslateIcon />,
        badge: isCurrent ? 'Active' : undefined,
        action: () => {
          setLanguage(code);
          handleClose();
        },
        keywords: `language translate ${name} ${code}`
      });
    });

    // --- CATEGORY: QUICK ACTIONS & COMMUNICATIONS ---
    const activeContact = contactInfo || {};
    const emailVal = activeContact.email || 'info@itforsec.com';
    const phoneVal = activeContact.phone || '+995 599-400-135';
    const whatsappVal = activeContact.whatsapp || '995599400135';

    list.push({
      id: 'act-copy-email',
      category: 'Quick Actions',
      title: copySuccess ? 'Copied to Clipboard!' : `Copy Email (${emailVal})`,
      subtitle: 'Copy ITFORSEC primary contact email to clipboard',
      icon: copySuccess ? <CheckCircleOutlineIcon color="success" /> : <ContentCopyIcon />,
      action: () => handleCopyEmail(emailVal),
      keywords: 'copy email info support address'
    });

    list.push({
      id: 'act-whatsapp',
      category: 'Quick Actions',
      title: 'Chat with Support on WhatsApp',
      subtitle: 'Open direct WhatsApp conversation for rapid inquiries',
      icon: <WhatsAppIcon sx={{ color: '#25D366' }} />,
      action: () => {
        const cleanNumber = whatsappVal.replace(/[^0-9]/g, '');
        window.open(`https://wa.me/${cleanNumber}`, '_blank', 'noopener,noreferrer');
        handleClose();
      },
      keywords: 'whatsapp chat instant message help support'
    });

    list.push({
      id: 'act-phone',
      category: 'Quick Actions',
      title: `Call Direct Hotline (${phoneVal})`,
      subtitle: 'Tbilisi, Georgia business hours hotline',
      icon: <PhoneOutlinedIcon />,
      action: () => {
        window.location.href = `tel:${phoneVal.replace(/\s+/g, '')}`;
        handleClose();
      },
      keywords: 'call telephone phone phone-call voice talk'
    });

    list.push({
      id: 'act-shortcuts',
      category: 'Help & Shortcuts',
      title: 'Keyboard Shortcuts Cheat Sheet',
      subtitle: 'View all keyboard accelerators and fast navigation keys',
      icon: <KeyboardIcon />,
      shortcut: '?',
      action: () => {
        setShowShortcutsCheatSheet(true);
      },
      keywords: 'shortcuts hotkeys keys accelerators help guide'
    });

    return list;
  }, [
    isSectionVisible,
    t,
    servicesList,
    language,
    isDarkMode,
    toggleTheme,
    availableLanguages,
    contactInfo,
    copySuccess,
    handleClose,
    setLanguage,
    navigateTo,
    handleCopyEmail
  ]);

  // Filter commands by search query
  const filteredCommands = useMemo(() => {
    if (!query.trim()) return allCommands;
    const q = query.toLowerCase().trim();
    return allCommands.filter((cmd) => {
      const matchTitle = cmd.title?.toLowerCase().includes(q);
      const matchSubtitle = cmd.subtitle?.toLowerCase().includes(q);
      const matchCategory = cmd.category?.toLowerCase().includes(q);
      const matchKeywords = cmd.keywords?.toLowerCase().includes(q);
      return matchTitle || matchSubtitle || matchCategory || matchKeywords;
    });
  }, [allCommands, query]);

  // Keep selected index within bounds
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Handle keyboard navigation inside the palette
  const handleInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % (filteredCommands.length || 1));
      scrollSelectedIntoView();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredCommands.length) % (filteredCommands.length || 1));
      scrollSelectedIntoView();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCommands[selectedIndex]) {
        filteredCommands[selectedIndex].action();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
    }
  };

  const scrollSelectedIntoView = () => {
    setTimeout(() => {
      const activeEl = listRef.current?.querySelector('.palette-item-active');
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }, 20);
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <>
      {/* 1. FLOATING ACTION TRIGGER PILL (IF ENABLED) */}
      {showFloatingTrigger && (
        <Tooltip title={`Open Command Palette (${modifierLabel}+${shortcutKey.toUpperCase()})`} placement="left" arrow>
          <Box
            onClick={handleOpen}
            role="button"
            tabIndex={0}
            aria-label="Open Command Palette"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpen();
              }
            }}
            sx={{
              position: 'fixed',
              bottom: { xs: 20, md: 28 },
              right: { xs: 20, md: 28 },
              zIndex: 1200,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.8,
              py: 0.9,
              borderRadius: '24px',
              backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(12px)',
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(46, 91, 255, 0.35)' : 'rgba(46, 91, 255, 0.25)',
              color: isDarkMode ? '#F8FAFC' : '#0F172A',
              boxShadow: isDarkMode
                ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 16px rgba(46, 91, 255, 0.25)'
                : '0 8px 24px rgba(46, 91, 255, 0.15)',
              cursor: 'pointer',
              transition: 'all 0.25s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                borderColor: '#2E5BFF',
                boxShadow: isDarkMode
                  ? '0 12px 36px rgba(46, 91, 255, 0.4)'
                  : '0 10px 28px rgba(46, 91, 255, 0.25)',
              },
              '&:active': {
                transform: 'scale(0.97)',
              }
            }}
          >
            <TerminalIcon sx={{ fontSize: 18, color: '#2E5BFF' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem', display: { xs: 'none', sm: 'inline' } }}>
              Commands
            </Typography>
            <Box
              component="kbd"
              sx={{
                fontSize: '0.72rem',
                fontFamily: 'monospace',
                px: 0.8,
                py: 0.2,
                borderRadius: '6px',
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                border: '1px solid',
                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
                color: isDarkMode ? '#94A3B8' : '#475569',
                fontWeight: 700,
                letterSpacing: 0.5
              }}
            >
              {modifierLabel}+{shortcutKey.toUpperCase()}
            </Box>
          </Box>
        </Tooltip>
      )}

      {/* 2. FULL INTERACTIVE COMMAND PALETTE DIALOG */}
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        transitionDuration={200}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            position: 'relative',
            borderRadius: '16px',
            backgroundColor: isDarkMode ? '#0B1120' : '#FFFFFF',
            backgroundImage: isDarkMode
              ? 'radial-gradient(circle at top left, rgba(46, 91, 255, 0.15), transparent 45%), radial-gradient(circle at bottom right, rgba(0, 200, 150, 0.1), transparent 45%)'
              : 'none',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(46, 91, 255, 0.3)' : 'rgba(46, 91, 255, 0.2)',
            boxShadow: isDarkMode
              ? '0 25px 60px rgba(0, 0, 0, 0.85), 0 0 40px rgba(46, 91, 255, 0.2)'
              : '0 20px 50px rgba(15, 23, 42, 0.2)',
            overflow: 'hidden',
            mt: { xs: 2, sm: 8 },
            mb: 'auto',
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: isDarkMode ? 'rgba(3, 7, 18, 0.75)' : 'rgba(15, 23, 42, 0.45)',
              backdropFilter: 'blur(8px)',
            }
          }
        }}
      >
        {/* PALETTE SEARCH HEADER */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2.5,
            py: 1.8,
            borderBottom: '1px solid',
            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
            gap: 1.5,
          }}
        >
          <SearchIcon sx={{ color: '#2E5BFF', fontSize: 24 }} />
          <InputBase
            inputRef={inputRef}
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            fullWidth
            sx={{
              fontSize: '1.05rem',
              color: isDarkMode ? '#F8FAFC' : '#0F172A',
              fontWeight: 500,
              '& input::placeholder': {
                color: isDarkMode ? '#64748B' : '#94A3B8',
                opacity: 1,
              }
            }}
          />
          {query && (
            <IconButton size="small" onClick={() => setQuery('')} sx={{ color: isDarkMode ? '#94A3B8' : '#64748B' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          <Box
            component="kbd"
            sx={{
              fontSize: '0.72rem',
              fontFamily: 'monospace',
              px: 1,
              py: 0.4,
              borderRadius: '6px',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)',
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)',
              color: isDarkMode ? '#94A3B8' : '#64748B',
              fontWeight: 600,
            }}
          >
            ESC
          </Box>
        </Box>

        {/* CHEAT SHEET MODE OR RESULTS LIST */}
        {showShortcutsCheatSheet ? (
          <Box sx={{ p: 3, maxHeight: '60vh', overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <KeyboardIcon fontSize="small" sx={{ color: '#2E5BFF' }} /> Keyboard Shortcuts Guide
              </Typography>
              <IconButton size="small" onClick={() => setShowShortcutsCheatSheet(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#94A3B8' : '#64748B', mb: 2 }}>
              Accelerate your navigation and site controls with single keystrokes:
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[
                { key: `${modifierLabel} + ${shortcutKey.toUpperCase()}`, desc: 'Open or close Command Palette from anywhere' },
                { key: '/', desc: 'Quick-open command search (when not in an input box)' },
                { key: '↑ / ↓', desc: 'Navigate up and down through matching commands' },
                { key: 'Enter ↵', desc: 'Execute the currently selected command' },
                { key: 'Escape', desc: 'Dismiss Command Palette' },
                { key: '?', desc: 'Toggle this keyboard shortcuts cheat sheet' },
                { key: 'T', desc: 'Switch theme between Cyber Dark and Clean Light' },
              ].map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.2,
                    borderRadius: '8px',
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {item.desc}
                  </Typography>
                  <Box
                    component="kbd"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      px: 1.2,
                      py: 0.4,
                      borderRadius: '6px',
                      backgroundColor: isDarkMode ? '#1E293B' : '#E2E8F0',
                      border: '1px solid',
                      borderColor: isDarkMode ? '#334155' : '#CBD5E1',
                      color: isDarkMode ? '#38BDF8' : '#0284C7',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.key}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <List
            ref={listRef}
            sx={{
              maxHeight: '52vh',
              overflowY: 'auto',
              p: 1.5,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
                borderRadius: '3px',
              }
            }}
          >
            {filteredCommands.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center', color: isDarkMode ? '#64748B' : '#94A3B8' }}>
                <SearchIcon sx={{ fontSize: 44, opacity: 0.3, mb: 1 }} />
                <Typography variant="body1" fontWeight={600}>
                  No matching commands found
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Try typing "services", "contact", "dark", or "language"
                </Typography>
              </Box>
            ) : (
              filteredCommands.map((item, idx) => {
                const isSelected = idx === selectedIndex;
                const showCategoryHeader = idx === 0 || filteredCommands[idx - 1].category !== item.category;

                return (
                  <React.Fragment key={item.id}>
                    {showCategoryHeader && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          px: 2,
                          pt: idx === 0 ? 0.5 : 2,
                          pb: 0.8,
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: 1.2,
                          color: isDarkMode ? '#64748B' : '#94A3B8',
                        }}
                      >
                        {item.category}
                      </Typography>
                    )}
                    <ListItem
                      button
                      onClick={item.action}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      className={isSelected ? 'palette-item-active' : ''}
                      sx={{
                        borderRadius: '10px',
                        mb: 0.5,
                        px: 1.8,
                        py: 1.1,
                        backgroundColor: isSelected
                          ? isDarkMode
                            ? 'rgba(46, 91, 255, 0.18)'
                            : 'rgba(46, 91, 255, 0.08)'
                          : 'transparent',
                        border: '1px solid',
                        borderColor: isSelected
                          ? isDarkMode
                            ? 'rgba(46, 91, 255, 0.5)'
                            : 'rgba(46, 91, 255, 0.3)'
                          : 'transparent',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 38,
                          color: isSelected
                            ? '#2E5BFF'
                            : isDarkMode
                              ? '#94A3B8'
                              : '#64748B',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            fontWeight={isSelected ? 600 : 500}
                            sx={{ color: isDarkMode ? '#F8FAFC' : '#0F172A' }}
                          >
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          item.subtitle && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDarkMode ? '#64748B' : '#64748B',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: { xs: 260, sm: 360 }
                              }}
                            >
                              {item.subtitle}
                            </Typography>
                          )
                        }
                      />
                      {item.shortcut && (
                        <Box
                          component="kbd"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            px: 0.9,
                            py: 0.3,
                            borderRadius: '5px',
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                            color: isDarkMode ? '#94A3B8' : '#475569',
                            ml: 1,
                          }}
                        >
                          {item.shortcut}
                        </Box>
                      )}
                      {item.badge && (
                        <Box
                          sx={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            px: 1,
                            py: 0.2,
                            borderRadius: '4px',
                            backgroundColor: isDarkMode ? 'rgba(0, 200, 150, 0.2)' : 'rgba(0, 200, 150, 0.15)',
                            color: isDarkMode ? '#00E5AA' : '#059669',
                            ml: 1,
                          }}
                        >
                          {item.badge}
                        </Box>
                      )}
                      {isSelected && (
                        <KeyboardReturnIcon
                          sx={{
                            fontSize: 16,
                            color: '#2E5BFF',
                            ml: 1,
                            opacity: 0.8,
                          }}
                        />
                      )}
                    </ListItem>
                  </React.Fragment>
                );
              })
            )}
          </List>
        )}

        {/* PALETTE FOOTER WITH HINTS */}
        <Divider sx={{ borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)' }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2.5,
            py: 1.2,
            backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : 'rgba(248, 250, 252, 0.8)',
            fontSize: '0.72rem',
            color: isDarkMode ? '#64748B' : '#94A3B8',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="kbd" sx={{ px: 0.6, py: 0.1, borderRadius: '4px', bgcolor: isDarkMode ? '#1E293B' : '#E2E8F0', color: isDarkMode ? '#CBD5E1' : '#475569', fontWeight: 600 }}>↑↓</Box> Navigate
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="kbd" sx={{ px: 0.6, py: 0.1, borderRadius: '4px', bgcolor: isDarkMode ? '#1E293B' : '#E2E8F0', color: isDarkMode ? '#CBD5E1' : '#475569', fontWeight: 600 }}>↵</Box> Select
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="kbd" sx={{ px: 0.6, py: 0.1, borderRadius: '4px', bgcolor: isDarkMode ? '#1E293B' : '#E2E8F0', color: isDarkMode ? '#CBD5E1' : '#475569', fontWeight: 600 }}>ESC</Box> Close
            </Box>
          </Box>

          <Box
            role="button"
            onClick={() => setShowShortcutsCheatSheet((prev) => !prev)}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontWeight: 600,
              color: '#2E5BFF',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            <Box component="kbd" sx={{ px: 0.6, py: 0.1, borderRadius: '4px', bgcolor: isDarkMode ? '#1E293B' : '#E2E8F0', color: isDarkMode ? '#CBD5E1' : '#475569' }}>?</Box>
            {showShortcutsCheatSheet ? 'Back to Commands' : 'Cheat Sheet'}
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default CommandPalette;
